import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
// TODO: типы вынести в types
import { UsersSearchParameters } from '@src/api';
import { SearchUsersService } from '@src/app/modules/search-users';
import { ActionItemModel } from '@src/app/modules/actions';
import { Nullable, Optional } from '@src/types/utils';

import { ApiService, DialogService, PermissionService, StoreService } from '../../services';
import { mapUserUIToAddMemberModel } from '../../utils';
import { ChatMemberModel, ChatPermissionsModel } from '../types';

@Component({
  selector: 'chat-members-list',
  templateUrl: './chat-members-list.component.html',
  styleUrls: ['./chat-members-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ChatMembersListComponent implements OnChanges, OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly searchUsersService = inject(SearchUsersService);
  private readonly apiService = inject(ApiService);
  private readonly dialogService = inject(DialogService);
  private readonly store = inject(StoreService);
  private readonly translateService = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);

  @Input() chatId?: string;
  @Input() permissions?: Nullable<ChatPermissionsModel>;

  members: Optional<Nullable<Array<ChatMemberModel>>>;

  headerActions: ActionItemModel[] = [];
  itemActions: ActionItemModel[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chatId']) {
      if (this.chatId) {
        this.refresh();
      }
    }

    if (changes['permissions']) {
      this.setHeaderActions();
      this.setItemActions();
    }

    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    this.store.chatMembers$.subscribe(chatMembers => {
      this.members = Object.keys(chatMembers).length ? Object.values(chatMembers[this.chatId!]) : [];
      this.cdr.markForCheck();
    });
  }

  openAddMembersDialog() {
    // TODO: сделать emit во вне

    const filter: UsersSearchParameters = {};
    const excludeUsersIds = this.members?.map(member => member.userId);

    this.searchUsersService
      .searchMultiple(undefined, undefined, excludeUsersIds, undefined, filter)
      .subscribe(users => {
        if (!users?.length) return;

        if (this.chatId && users.length) {
          const members = mapUserUIToAddMemberModel(users);
          if (members.length) {
            this.apiService.addChatMembers(this.chatId, members).then();
          } else {
            // TODO: вывести ошибку
          }
        }
        this.cdr.markForCheck();
      });
  }

  openConfirmMakeAdminDialog(member: ChatMemberModel): void {
    this.dialogService.confirmMakeAdminDialog.subscribe({
      next: res => {
        if (res) {
          this.makeAdmin(member);
        }
      },
    });
  }

  openConfirmDemoteAdminDialog(userId: string): void {
    this.dialogService.confirmDemoteAdminDialog.subscribe({
      next: res => {
        if (res) {
          this.demoteAdmin(userId);
        }
      },
    });
  }

  openConfirmRemoveChatMemberDialog(userId: string): void {
    this.dialogService.confirmRemoveChatMemberDialog.subscribe({
      next: res => {
        if (res) {
          this.removeChatMember(userId);
        }
      },
    });
  }

  private setHeaderActions() {
    this.headerActions = [
      {
        title: this.translateService.instant('components.internalMessenger.buttons.addMembers'),
        icon: 'user-plus',
        visible: () => {
          return !!this.permissions?.canManageUsers;
        },
        action: () => {
          this.openAddMembersDialog();
        },
      },
    ];
  }

  private setItemActions() {
    this.itemActions = [
      {
        title: this.translateService.instant('components.internalMessenger.buttons.makeAdmin'),
        icon: 'shield-check',
        color: 'var(--int-mes-color-blue)',
        visible: (item: ChatMemberModel) => {
          return !!this.permissions?.canManageAdmins && !item.isChatAdmin;
        },
        action: (item: ChatMemberModel) => {
          this.openConfirmMakeAdminDialog(item);
        },
      },
      {
        title: this.translateService.instant('components.internalMessenger.buttons.demoteAdmin'),
        icon: 'shield-x',
        color: 'var(--int-mes-color-red)',
        visible: (item: ChatMemberModel) => {
          return (
            !!this.permissions?.canManageAdmins && !!item.isChatAdmin && !this.permissionService.isAuthUser(item.userId)
          );
        },
        action: (item: ChatMemberModel) => {
          this.openConfirmDemoteAdminDialog(item.userId);
        },
      },
      {
        title: this.translateService.instant('components.internalMessenger.buttons.deleteMember'),
        icon: 'x',
        color: 'var(--int-mes-color-red)',
        visible: (item: ChatMemberModel) => {
          // TODO: Уточнить у бэка, можно ли сразу удалять админа из чата
          return !!this.permissions?.canManageUsers && !item.isChatAdmin;
        },
        action: (item: ChatMemberModel) => {
          this.openConfirmRemoveChatMemberDialog(item.userId);
        },
      },
    ];
  }

  private makeAdmin(member: ChatMemberModel) {
    if (!this.chatId) {
      // TODO: вывести ошибку
      return;
    }

    this.apiService.addAdmin({ chatId: this.chatId, userId: member.userId }).subscribe();
  }

  private demoteAdmin(userId: string) {
    if (!this.chatId) {
      // TODO: вывести ошибку
      return;
    }

    this.apiService.removeAdmin({ chatId: this.chatId, userId }).subscribe();
  }

  private removeChatMember(userId: string) {
    if (!this.chatId) {
      // TODO: вывести ошибку
      return;
    }

    this.apiService.removeChatMember(this.chatId, userId).subscribe();
  }

  private refresh() {
    if (!this.chatId) {
      // TODO: вывести ошибку
      return;
    }

    this.apiService.getChatMembers(this.chatId).subscribe();
  }
}
